import { computed, inject } from '@angular/core';
import { PointApi } from '@app/api/point.api';
import { PointToApprove } from '@app/models';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { interval, map, pipe, switchMap, tap } from 'rxjs';

interface PointState {
  listToApprove: PointToApprove[];
  isLoading: boolean;
}

const initialState: PointState = {
  listToApprove: [],
  isLoading: false,
};

export const PointStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withComputed((store) => ({
    notifyCount: computed(() => store.listToApprove().length),
  })),

  withMethods((store) => {
    const pointApi = inject(PointApi);

    const loadPointsToApprove = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { isLoading: true })),
        switchMap(() =>
          pointApi
            .getListToApprove$()
            .pipe(
              tap((listToApprove) =>
                patchState(store, { listToApprove, isLoading: false }),
              ),
            ),
        ),
      ),
    );

    const approvePoint = rxMethod<{
      orderId: number;
      pointId: number;
      onSuccess: () => void;
    }>(
      pipe(
        switchMap(({ orderId, pointId, onSuccess }) =>
          pointApi.setPointApprove$(orderId, pointId).pipe(
            tap(() => {
              patchState(store, {
                listToApprove: store
                  .listToApprove()
                  .filter((point) => point.id !== pointId),
              });
            }),
            map(() => onSuccess),
          ),
        ),
        tap((onSuccess) => onSuccess()),
      ),
    );

    const startPollingPointsToApprove = rxMethod<void>(
      pipe(
        switchMap(() =>
          interval(1000 * 30).pipe(tap(() => loadPointsToApprove())),
        ),
      ),
    );

    const clearPoints = () => patchState(store, initialState);

    return {
      loadPointsToApprove,
      startPollingPointsToApprove,
      approvePoint,
      clearPoints,
    };
  }),
);
